import React from 'react';

const InspectionBtn = ({
    children,
    variant = 'primary',
    size = 'default',
    icon,
    loading = false,
    disabled = false,
    onClick,
    className = '',
    type = 'button',
    ...props
}) => {
    // Base styles for professional mobile button
    const baseClasses = `
        inline-flex items-center justify-center gap-1 pb-[0.3px]
        rounded-full font-medium transition-all duration-200 
        border-0 outline-none focus:outline-none focus:ring-2 focus:ring-offset-2
        select-none cursor-pointer
        ${disabled || loading ? 'cursor-not-allowed' : 'active:scale-95 hover:shadow-lg'}
    `;

    // Size variants optimized for mobile touch - reduced sizes
    const sizeClasses = {
        xs: "h-6 px-2 text-xs min-w-[40px] shadow-sm focus:ring-1 focus:ring-offset-1",
        small: "h-7 px-3 text-xs min-w-[52px] shadow-sm focus:ring-1 focus:ring-offset-1",
        default: "h-8 px-4 text-sm min-w-[64px] shadow-md focus:ring-2 focus:ring-offset-1",
        large: "h-9 px-5 text-sm min-w-[80px] shadow-md focus:ring-2 focus:ring-offset-2"
    };

    // Professional color variants using theme color
    const variantClasses = {
        primary: `
             bg-gradient-to-r from-[#0087af] to-[#00ae68] hover:bg-[#238a70] active: bg-gradient-to-r from-[#0087af] to-[#00ae68] 
            text-white rounded-xl
             focus:ring-[#289E80]/30
            ${disabled || loading ? 'opacity-60 hover:bg-[#289E80] active:bg-[#289E80] hover:shadow-[#289E80]/25' : ''}
        `,
        secondary: `
            bg-white hover:bg-gray-50 active:bg-gray-100 
            text-[#289E80] 
            ring-2 ring-[#289E80] hover:ring-[#238a70] 
            shadow-gray-200/50 hover:shadow-gray-300/60
            focus:ring-[#289E80]/30
            ${disabled || loading ? 'opacity-60 hover:bg-white active:bg-white ring-[#289E80]/50' : ''}
        `,
        outline: `
            bg-transparent hover:bg-[#289E80]/5 active:bg-[#289E80]/10 
            text-[#289E80] 
            ring-2 ring-[#289E80] hover:ring-[#238a70] 
            shadow-none hover:shadow-sm
            focus:ring-[#289E80]/30
            ${disabled || loading ? 'opacity-60 hover:bg-transparent active:bg-transparent ring-[#289E80]/50' : ''}
        `,
        ghost: `
            bg-transparent hover:bg-[#289E80]/8 active:bg-[#289E80]/12 
            text-[#289E80] 
            shadow-none hover:shadow-sm
            focus:ring-[#289E80]/30
            ${disabled || loading ? 'opacity-60 hover:bg-transparent active:bg-transparent' : ''}
        `,
        danger: `
            bg-red-500 hover:bg-red-600 active:bg-red-700 
            text-white 
            shadow-red-500/25 hover:shadow-red-500/40
            focus:ring-red-500/30
            ${disabled || loading ? 'opacity-60 hover:bg-red-500 active:bg-red-500' : ''}
        `,
        success: `
            bg-green-500 hover:bg-green-600 active:bg-green-700 
            text-white 
            shadow-green-500/25 hover:shadow-green-500/40
            focus:ring-green-500/30
            ${disabled || loading ? 'opacity-60 hover:bg-green-500 active:bg-green-500' : ''}
        `
    };

    // Loading spinner component
    const LoadingSpinner = ({ size }) => {
        const spinnerSize = {
            xs: 'w-3 h-3',
            small: 'w-3 h-3',
            default: 'w-4 h-4',
            large: 'w-4 h-4'
        }[size];

        return (
            <div className={`${spinnerSize} border-2 border-current border-t-transparent rounded-full animate-spin`} />
        );
    };

    const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`.trim();

    return (
        <button
            type={type}
            className={combinedClasses}
            disabled={disabled || loading}
            onClick={!disabled && !loading ? onClick : undefined}
            style={{
                boxShadow: disabled ? 'none' : undefined,
            }}
            {...props}
        >
            {loading ? (
                <LoadingSpinner size={size} />
            ) : icon ? (
                <span className="inline-flex items-center justify-center">
                    {icon}
                </span>
            ) : null}

            {children && (
                <span className={loading || icon ? 'ml-1' : ''}>
                    {children}
                </span>
            )}
        </button>
    );
};

export default InspectionBtn;